import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { useReactTable, getCoreRowModel, createColumnHelper } from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import StatCard from "./ReportsCards";
import { showError, showSuccess } from "toast/toast";
import { Supplier } from "types";

import { getAuthCookie } from "../utils/authCookies";
import { fetchMarketplaceProfilesSelect2 } from "api/postTypeApiCalls";

type CardsSummary = {
    total_orders: number;        // sum(removals)
    total_revenue: number;       // sum(total_billed)
    amount_paid: number;         // sum(paid)
    outstanding_balance: number; // sum(balance)
};

interface Row {
    agent_name: string;
    office_name: string;
    removals: number;
    payment_method: string;
    total_billed: number;
    paid: number;
    balance: number;
    over_paid: boolean;
}

type SelectOption = { label: string; value: string };

const DATE_OPTIONS: SelectOption[] = [
    { label: "Last 7 Days", value: "last_7_days" },
    { label: "Last 15 Days", value: "last_15_days" },
    { label: "Last 30 Days", value: "last_30_days" },
    { label: "Custom", value: "custom" },
];

const PAYMENT_METHOD_OPTIONS: SelectOption[] = [
    { label: "All", value: "all" },
    { label: "Card", value: "Card" },
    { label: "Net 30", value: "Net 30" },
    { label: "Bank Transfer", value: "Bank Transfer" },
];

const PAYMENT_STATUS_OPTIONS: SelectOption[] = [
    { label: "All", value: "all" },
    { label: "Paid", value: "Paid" },
    { label: "Partial Payment", value: "Partial Payment" },
    { label: "Pending", value: "Pending" },
    { label: "Overdue", value: "Overdue" },
];

const DropdownIndicator = (props: any) => {
    if (props.selectProps.isDisabled) return null;
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
        </components.DropdownIndicator>
    );
};

const customSelectStyles = {
    indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
    dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
    indicatorSeparator: () => ({ display: "none" }),
    control: (p: any, s: any) => ({
        ...p,
        backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
        cursor: s.isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
    }),
    singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
    placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
    menu: (provided: any) => ({ ...provided, zIndex: 9999, position: "absolute", top: "100%", left: 0, right: 0 }),
    menuList: (provided: any) => ({ ...provided, maxHeight: "220px" }),
};

export default function BrokerageRemovalSummaryReport() {
    const token = getAuthCookie("sessionToken");
    const organizationId = getAuthCookie("organizationId");
    const membershipId = getAuthCookie("membershipId");
    const location = useLocation();

    // Detect login type
    const isTeammember = location.pathname.includes('/teammember');
    const isRealtor = location.pathname.includes('/realtor');

    // State for parent brokerage firm (for teammember login) — use organizationId cookie instead

    const fmtMoney = (n: number) =>
        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(Number(n || 0));

    // data + table
    const [data, setData] = useState<Row[]>([]);
    const [rowCount, setRowCount] = useState(0);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

    // filters
    const [datePreset, setDatePreset] = useState<SelectOption>(DATE_OPTIONS[2]); // Last 30 Days
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [paymentStatus, setPaymentStatus] = useState<SelectOption>(PAYMENT_STATUS_OPTIONS[0]); // All
    const [paymentMethod, setPaymentMethod] = useState<SelectOption>(PAYMENT_METHOD_OPTIONS[0]); // All

    const [officeOptions, setOfficeOptions] = useState<SelectOption[]>([]);
    const [officeSearch, setOfficeSearch] = useState<string>("");
    const [selectedOffice, setSelectedOffice] = useState<SelectOption | null>(null);

    const [agentOptions, setAgentOptions] = useState<SelectOption[]>([{ label: "All", value: "" }]);
    const [selectedAgent, setSelectedAgent] = useState<SelectOption | null>({ label: "All", value: "" });
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [supplierOptions, setSupplierOptions] = useState<SelectOption[]>([{ label: "All", value: "" }]);
    const [selectedSupplier, setSelectedSupplier] = useState<SelectOption | null>({ label: "All", value: "" });

    // cards
    const [cards, setCards] = useState<CardsSummary>({
        total_orders: 0,
        total_revenue: 0,
        amount_paid: 0,
        outstanding_balance: 0,
    });

    // UI state
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [globalFilter, setGlobalFilter] = useState("");
    const topRef = useRef<HTMLDivElement>(null);
    
    // Calculate date range limits (last 6 months from today)
    const dateRangeLimits = useMemo(() => {
        const today = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        
        // Format as YYYY-MM-DD for date input
        const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            minDate: formatDate(sixMonthsAgo),
            maxDate: formatDate(today)
        };
    }, []);

    // Validate and reset dates when custom is selected or dates are out of range
    useEffect(() => {
        if (datePreset.value === "custom") {
            const minDate = dateRangeLimits.minDate;
            const maxDate = dateRangeLimits.maxDate;
            
            // Reset From date if it's outside the valid range
            if (customFrom && (customFrom < minDate || customFrom > maxDate)) {
                setCustomFrom("");
            }
            
            // Reset To date if it's outside the valid range
            if (customTo && (customTo < minDate || customTo > maxDate)) {
                setCustomTo("");
            }
        }
    }, [datePreset.value, dateRangeLimits, customFrom, customTo]);

    // actions
    const [generateClicked, setGenerateClicked] = useState(false);
    function handleGenerate() {
        if (!token) {
            toast.error("Session expired. Please sign in again.");
            return;
        }
        setPagination((p) => ({ ...p, pageIndex: 0 }));
        setGenerateClicked((v) => !v);
    }
    function clearFilters() {
        setDatePreset(DATE_OPTIONS[2]);
        setCustomFrom(""); setCustomTo("");
        setPaymentStatus(PAYMENT_STATUS_OPTIONS[0]);
        setPaymentMethod(PAYMENT_METHOD_OPTIONS[0]);
        setSelectedOffice(null);
        setAgentOptions([{ label: "All", value: "" }]);
        setSelectedAgent({ label: "All", value: "" });
        setSupplierOptions([{ label: "All", value: "" }]);
        setSelectedSupplier({ label: "All", value: "" });
        setSearchInput("");
        setGlobalFilter("");
        setPagination({ pageIndex: 0, pageSize: 10 });
        setGenerateClicked((v) => !v);
    }

    // fetch offices for current brokerage (organizationId from session)
    useEffect(() => {
        async function fetchOffices() {
            if (!organizationId) return;
            try {
                const r = await fetch(`${process.env.REACT_APP_API_URL}/organizations/${organizationId}/offices/list`, {
                    headers: { Authorization: `Bearer ${token || ""}`, "Content-Type": "application/json" },
                });
                const j = await r.json();
                if (!r.ok) {
                    toast.error(j?.error || "Failed to fetch offices.");
                    return;
                }
                const opts = (j || []).map((o: any) => ({ label: o.officeName, value: o._id }));
                setOfficeOptions(opts);
            } catch {
                toast.error("Network error while fetching offices.");
            }
        }
        fetchOffices();
    }, [organizationId, token]);

    // brokerage id for API calls
    const brokerageId = String(organizationId || "").trim();

    // Async loader for agents: if office selected -> office agents, else brokerage agents via users-search
    const PAGE_SIZE = 20;
    const loadAgentOptions: LoadOptions<{ value: string; label: string }, any, { page: number }> = async (
        inputValue,
        _loadedOptions,
        additional
    ) => {
        const page = additional?.page ?? 1;
        try {
            if (selectedOffice?.value) {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/agent/office/${selectedOffice.value}`, {
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                });
                if (!res.ok) return { options: [], hasMore: false, additional: { page } };
                const data = await res.json();
                const options = (Array.isArray(data) ? data : data.data || [])
                    .map((a: any) => ({
                        value: String(a.membership_id || a.membershipId || a.id || a._id || "").trim(),
                        label: `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email || String(a.membership_id || a.id || a._id || ""),
                    }))
                    .filter((o: SelectOption) => o.value);
                return { options, hasMore: false, additional: { page: page + 1 } };
            }

            const params = new URLSearchParams({
                licenseType: "BROKERAGE_AGENT",
                brokerageFirmID: organizationId ?? "",
                page: String(page),
                limit: String(PAGE_SIZE),
            });
            const q = (inputValue || "").trim();
            if (q) params.set("search", q);
            const url = `${process.env.REACT_APP_API_URL}/organization-profiles/search?${params.toString()}`;
            const response = await fetch(url, {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) return { options: [], hasMore: false, additional: { page } };
            const json = await response.json();
            const results = Array.isArray(json) ? json : json.results || [];
            const hasMore = Array.isArray(json) ? results.length === PAGE_SIZE : json.pagination?.more || false;
            const options = results
                .map((u: any) => ({
                    value: String(u.membership_id ?? u.id ?? u._id ?? "").trim(),
                    label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || String(u.membership_id ?? u.id ?? u._id ?? ""),
                }))
                .filter((o: SelectOption) => o.value);
            return { options, hasMore, additional: { page: page + 1 } };
        } catch (err) {
            console.error("Failed to load agent options:", err);
            return { options: [], hasMore: false, additional: { page } };
        }
    };
    // fetch agents for selected office
    useEffect(() => {
        async function fetchAgents() {
            if (!selectedOffice?.value) {
                setAgentOptions([{ label: "All", value: "" }]);
                setSelectedAgent({ label: "All", value: "" });
                return;
            }
            try {
                const r = await fetch(`${process.env.REACT_APP_API_URL}/agent/office/${selectedOffice.value}`, {
                    headers: { Authorization: `Bearer ${token || ""}`, "Content-Type": "application/json" },
                });
                const j = await r.json();
                if (!r.ok) {
                    toast.error(j?.error || "Failed to fetch agents.");
                    setAgentOptions([{ label: "All", value: "" }]);
                    setSelectedAgent({ label: "All", value: "" });
                    return;
                }
                const opts = [
                    { label: "All", value: "" },
                    ...(j || [])
                        .map((a: any) => ({
                            label: `${a.firstName || ""} ${a.lastName || ""}`.trim(),
                            value: String(a.membership_id || a.membershipId || a.id || "").trim(),
                        }))
                        .filter((o: SelectOption) => o.value),
                ];
                setAgentOptions(opts);
                setSelectedAgent(opts[0]);
            } catch {
                toast.error("Network error while fetching agents.");
                setAgentOptions([{ label: "All", value: "" }]);
                setSelectedAgent({ label: "All", value: "" });
            }
        }
        fetchAgents();
    }, [selectedOffice, token]);

    // fetch suppliers (maps to seller_org_id in payload)
    useEffect(() => {
        const fetchSuppliersByUserId = async () => {
            if (!token) return;

            try {
                const { results } = await fetchMarketplaceProfilesSelect2(token, {
                    category: "signage",
                    page: 1,
                    limit: 100,
                });
                const installerOptions = (results || [])
                    .map((r: any) => ({
                        label: String(r.text || "").trim(),
                        value: String(r.organization_id || "").trim(),
                    }))
                    .filter((o: SelectOption) => o.label && o.value);
                setSuppliers(
                    installerOptions.map((o) => ({
                        supplier_id: o.value,
                        name: o.label,
                        email: "",
                    })) as Supplier[]
                );
                setSupplierOptions([{ label: "All", value: "" }, ...installerOptions]);
            } catch (err) {
                showError("Failed to fetch supplier list");
            }
        };
        fetchSuppliersByUserId();
    }, [token]);

    // fetch report
    async function fetchData(signal?: AbortSignal) {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/reports/brokerage/removal_billing_summary`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token || ""}`, "Content-Type": "application/json" },
                    signal,
                    body: JSON.stringify({
                        page: pagination.pageIndex + 1,
                        limit: pagination.pageSize,
                        brokerage_firm_org_id: organizationId || undefined,
                        office_id: selectedOffice?.value || undefined,
                        agent_membership_id: selectedAgent?.value || undefined,
                        seller_org_id: selectedSupplier?.value || undefined,
                        payment_status: paymentStatus.value === "all" ? "" : paymentStatus.value,
                        payment_method: paymentMethod.value === "all" ? "" : paymentMethod.value,
                        search: globalFilter,
                        date_range: {
                            type: datePreset.value,
                            from: datePreset.value === "custom" ? customFrom : "",
                            to: datePreset.value === "custom" ? customTo : "",
                        },
                    }),
                }
            );
            const result = await res.json();

            if (!res.ok) {
                toast.error(result?.error || "Failed to fetch brokerage removal summary.");
                setData([]); setRowCount(0);
                setCards({ total_orders: 0, total_revenue: 0, amount_paid: 0, outstanding_balance: 0 });
                return;
            }

            const rows: Row[] = Array.isArray(result?.data) ? result.data : [];
            setData(rows);
            setRowCount(Number(result?.total || rows.length || 0));

            if (result?.cards) {
                setCards({
                    total_orders: Number(result.cards.total_orders || 0),
                    total_revenue: Number(result.cards.total_revenue || 0),
                    amount_paid: Number(result.cards.amount_paid || 0),
                    outstanding_balance: Number(result.cards.outstanding_balance || 0),
                });
            } else {
                const totals = rows.reduce(
                    (acc, r) => {
                        acc.total_orders += Number(r.removals || 0);
                        acc.total_revenue += Number(r.total_billed || 0);
                        acc.amount_paid += Number(r.paid || 0);
                        acc.outstanding_balance += Number(r.balance || 0);
                        return acc;
                    },
                    { total_orders: 0, total_revenue: 0, amount_paid: 0, outstanding_balance: 0 }
                );
                setCards(totals);
            }
        } catch (e: any) {
            if (e?.name === "AbortError") return;
            toast.error("Network error while fetching brokerage removal summary.");
            setData([]); setRowCount(0);
            setCards({ total_orders: 0, total_revenue: 0, amount_paid: 0, outstanding_balance: 0 });
        }
    }

    useEffect(() => {
        const ac = new AbortController();
        fetchData(ac.signal);
        return () => ac.abort();
        // eslint-disable-next-line
    }, [generateClicked, pagination.pageIndex, pagination.pageSize, globalFilter]);

    // columns — EXACTLY per your spec
    const columnHelper = createColumnHelper<Row>();
    const columns = useMemo(() => {
        return [
            columnHelper.display({
                id: "serial",
                header: () => <div className="th-text S-No">S.No.</div>,
                cell: (info) => (
                    <div className="text-sm S-No">
                        {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
                    </div>
                ),
            }),
            columnHelper.accessor("agent_name", {
                header: () => <div className="th-text">Agent Name</div>,
                cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
            }),
            columnHelper.accessor("office_name", {
                header: () => <div className="th-text">Office</div>,
                cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
            }),
            columnHelper.accessor("removals", {
                header: () => <div className="th-text">Removals</div>,
                cell: (info) => <div className="text-sm">{info.getValue() ?? 0}</div>,
            }),
            // columnHelper.accessor("payment_method", {
            //     header: () => <div className="th-text">Payment Method</div>,
            //     cell: (info) => <div className="text-sm">{info.getValue() || "--"}</div>,
            // }),
            columnHelper.accessor("total_billed", {
                header: () => <div className="th-text">Total Billed</div>,
                cell: (info) => <div className="fw-medium text-sm">{fmtMoney(Number(info.getValue() ?? 0))}</div>,
            }),
            columnHelper.accessor("paid", {
                header: () => <div className="th-text">Paid</div>,
                cell: (info) => <div className="text-sm" style={{ color: "#00a63e" }}>{fmtMoney(Number(info.getValue() ?? 0))}</div>,
            }),
            columnHelper.accessor("balance", {
                header: () => <div className="th-text">Balance</div>,
                cell: (info) => {
                    const overPaid = info.row.original.over_paid;
                    const balanceValue = Number(info.getValue() ?? 0);
                    return (
                        <div className="text-sm fw-medium" style={{ color: overPaid ? "#00a63e" : "#e7000b" }}>
                            {overPaid ? `+${fmtMoney(balanceValue)}` : `-${fmtMoney(balanceValue)}`}
                        </div>
                    );
                },
            }),
        ];
        // eslint-disable-next-line
    }, [pagination.pageIndex, pagination.pageSize]);

    const table = useReactTable({
        data: data || [],
        columns,
        pageCount: Math.ceil(rowCount / pagination.pageSize),
        state: { pagination },
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        onPaginationChange: setPagination,
    });

    useEffect(() => {
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [pagination.pageIndex]);

    return (
        <div className="content" ref={topRef}>
            {/* Header */}
            <div className="mb-4">
                <div>
                    <h4 className="mb-1 po-title">Removal Order Summary</h4>
                    <p className="po-subtitle">Billing & payment view of removals by office and agent</p>
                </div>
            </div>

            {/* Cards */}
            <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6 col-xl-3">
                    <StatCard label="Total Removals" value={cards?.total_orders ?? 0} money={false} accent="blue" icon="ri-settings-3-line" />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                    <StatCard label="Total Billed" value={cards?.total_revenue ?? 0} money accent="green" icon="bi-graph-up-arrow" />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                    <StatCard label="Paid" value={cards?.amount_paid ?? 0} money accent="green" icon="bi-currency-dollar" />
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                    <StatCard label="Balance" value={cards?.outstanding_balance ?? 0} money accent="red" icon="bi-exclamation-octagon" />
                </div>
            </div>

            {/* Filters */}
            <div className="po-header mb-4 shadow-border-datatables">
                {/* TOP ROW */}
                <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                    <div className="po-title-wrap me-2">
                    </div>

                    {/* tools block (search + buttons) */}
                    <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
                        {/* Search */}
                        <div className="input-group po-search ">
                            <span className="input-group-text">
                                <i className="ri-search-line" />
                            </span>
                            <input
                                type="text"
                                id="searchInput"
                                name="searchInput"
                                className="form-control"
                                placeholder="Search by Agent, Office..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") setGlobalFilter(searchInput); }}
                            />
                        </div>

                        {/* Filters toggle */}
                        <button
                            type="button"
                            className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
                            onClick={() => setFiltersOpen(v => !v)}
                            aria-expanded={filtersOpen}
                            aria-controls="incoming-filters"
                        >
                            <i className="ri-filter-3-line me-2" />
                            <span>Filters</span>
                            <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
                        </button>
                    </div>
                </div>

                <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`} style={{ overflow: "visible" }}>
                    <div className="po-filters-bar d-flex">
                        <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
                            <div className="col-lg-2">
                                <label className="form-label mb-1 label-1">Date Range</label>
                                <Select
                                    options={DATE_OPTIONS}
                                    value={datePreset}
                                    onChange={(v) => v && setDatePreset(v)}
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                            </div>

                            {datePreset.value === "custom" && (
                                <>
                                    <div className="col-lg-2">
                                        <label className="form-label mb-1 label-1">From</label>
                                        <input 
                                            type="date" 
                                            className="form-control" 
                                            value={customFrom} 
                                            onChange={(e) => setCustomFrom(e.target.value)}
                                            min={dateRangeLimits.minDate}
                                            max={dateRangeLimits.maxDate}
                                        />
                                    </div>
                                    <div className="col-lg-2">
                                        <label className="form-label mb-1 label-1">To</label>
                                        <input 
                                            type="date" 
                                            className="form-control" 
                                            value={customTo} 
                                            onChange={(e) => setCustomTo(e.target.value)}
                                            min={dateRangeLimits.minDate}
                                            max={dateRangeLimits.maxDate}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="col-lg-2">
                                <label className="form-label mb-1 label-1">Office</label>
                                <Select
                                    options={
                                        officeSearch
                                            ? officeOptions.filter(o => o.label.toLowerCase().includes(officeSearch.toLowerCase()))
                                            : officeOptions.slice(0, 5)
                                    }
                                    value={selectedOffice}
                                    onInputChange={(val) => setOfficeSearch(val)}
                                    onChange={(v) => setSelectedOffice(v || null)}
                                    isSearchable
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                    placeholder="Select Office"
                                />
                            </div>

                            <div className="col-lg-2">
                                <label className="form-label mb-1 label-1">Agent</label>
                                <AsyncPaginate
                                    value={selectedAgent}
                                    loadOptions={loadAgentOptions}
                                    onChange={(val: any) => setSelectedAgent(val ?? null)}
                                    additional={{ page: 1 }}
                                    placeholder={selectedOffice ? "Select Agent" : "Select Office first"}
                                    isDisabled={!brokerageId && !selectedOffice}
                                    debounceTimeout={300}
                                    defaultOptions
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                            </div>


                            <div className="col-lg-2">
                                <label className="form-label mb-1 label-1">Supplier</label>
                                <Select
                                    options={supplierOptions}
                                    value={selectedSupplier}
                                    onChange={(v) => setSelectedSupplier(v || null)}
                                    isSearchable
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                    placeholder="Select Supplier"
                                />
                            </div>

                            <div className="col-lg-2">
                                <label className="form-label mb-1 label-1">Payment Status</label>
                                <Select
                                    options={PAYMENT_STATUS_OPTIONS}
                                    value={paymentStatus}
                                    onChange={(v) => v && setPaymentStatus(v)}
                                    components={{ DropdownIndicator }}
                                    styles={customSelectStyles}
                                    classNamePrefix="rs"
                                />
                            </div>

                            <div className="col-lg-auto d-flex align-items-end gap-2">
                                <button className="btn btn-primary table-addbutton br-8" onClick={handleGenerate}>Apply Filter</button>
                                <button className="btn back-btn surface-btn fw-bold fs-16" onClick={clearFilters}>Clear</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
                <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
                    <div className="d-flex align-items-center">
                        <i className="ri-file-text-line me-2" aria-hidden="true" style={{ fontSize: 18, color: "#2563eb", lineHeight: 1 }} />
                        <h5 className="mb-0">Removal Data</h5>
                    </div>
                </div>
                <hr className="mb-2" style={{ borderTop: "none" }} />

                {/* Desktop / Tablet */}
                <div className="rdt-desktop">
                    <div className="production-scroll" style={{ "--rdt-min": "1100px" } as React.CSSProperties}>
                        <table className="table production-table align-middle mb-0 table-1">
                            <DesktopTableParts table={table} />
                        </table>
                    </div>
                </div>

                {/* Mobile */}
                <div className="rdt-mobile">
                    <MobileCardsOnly
                        table={table}
                        idColId="agent_name"
                        titleColId="office_name"
                        actionColId="balance"
                    />
                </div>
            </div>

            <div className="mt-4 mb-4">
                <TablePaginationControls table={table} />
            </div>
        </div>
    );
}